import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import LayoutContainer from 'components/Common/LayoutContainer'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserEligibilityTier from 'loyalty/hooks/useLoyaltyUserEligibilityTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import LoyaltyProgramNameLogo from '../LoyaltyProgramNameLogo'
import LoyaltyTierPill from '../LoyaltyTierPill'
import LoyaltyProductLogo from '../LoyaltyProductLogo'

// Infinite scroll animation for mobile
const infiniteScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(calc(-50% - ${rem(16)}));
  }
`

const Container = styled.div`
  padding-block: ${rem(12)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const MobileScrollContainer = styled.div`
  /* Mobile only - hide on tablet and up */
  ${mediaQueryUp.tablet} {
    display: none;
  }
  
  overflow: hidden;
  white-space: nowrap;
  position: relative;
`

const ScrollingContent = styled.div`
  display: inline-flex;
  animation: ${infiniteScroll} 16s linear infinite;
  align-items: center;
  gap: ${rem(32)};
  
  /* Pause animation on hover for better UX */
  &:hover {
    animation-play-state: paused;
  }
`

const ScrollItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(12)};
  white-space: nowrap;
  flex-shrink: 0;
`

const DesktopContainer = styled.div`
  /* Hide on mobile, show on tablet and up */
  display: none;
  
  ${mediaQueryUp.tablet} {
    display: block;
  }
`

function LoyaltyMembershipActivationPackageUpgradeBanner() {
  const [accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError] = useLoyaltyUserEligibilityTier()
  const givenName = useAppSelector(state => state.auth.account.givenName)

  const dispatch = useAppDispatch()
  const activateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dispatch])

  const pillMessage = useMemo(() => {
    switch (accountEligibleTier) {
      case 'silver':
        return 'New Silver benefit'
      case 'gold':
        return 'Upgrade Gold benefit'
      case 'bronze':
        return 'New benefits'
      case 'platinum':
      default:
        return ''
    }
  }, [accountEligibleTier])

  const coloredDescription = useMemo(() => {
    switch (accountEligibleTier) {
      case 'silver':
        return 'free room or package upgrade'
      case 'gold':
        return '2 free room or package upgrade'
      case 'platinum':
      default:
        return ''
    }
  }, [accountEligibleTier])

  // Helper function to render banner content for mobile scroll
  const renderMobileBannerContent = useCallback(() => {
    if (!accountEligibleTier) return null

    if (accountEligibleTier === 'bronze') {
      return (
        <ScrollItem>
          <LoyaltyProductLogo height={24} />
          <LoyaltyTierPill tier={accountEligibleTier} kind="primary" icon={null}>{pillMessage}</LoyaltyTierPill>
          <BodyText variant="medium">
            Even more reasons to activate your membership and book today!
            {' '}
            <TextLink size="medium" onClick={activateMembership}>Join for free</TextLink>
          </BodyText>
        </ScrollItem>
      )
    }

    const handleScrollItemClick = (e: React.MouseEvent) => {
      // Prevent activation if clicking on the TextLink
      if ((e.target as HTMLElement).closest('a')) {
        return
      }
      // Activate membership
      activateMembership()
    }

    return (
      <ScrollItem onClick={handleScrollItemClick}>
        <LoyaltyProgramNameLogo height={24} />
        <LoyaltyTierPill tier={accountEligibleTier} kind="primary">{pillMessage}</LoyaltyTierPill>
        <BodyText variant="medium">
          {!!givenName && <>{givenName}, you've got</>}
          {!givenName && <>You've got</>}
          {' '}
          <Colour value={`tier-${accountEligibleTier}-light-contrast`} as="b">
            {coloredDescription}
          </Colour>
          {' '}
          <TextLink size="medium" onClick={activateMembership}>Activate {getLoyaltyTierDisplayName(accountEligibleTier)} Status free</TextLink>
        </BodyText>
      </ScrollItem>
    )
  }, [accountEligibleTier, pillMessage, givenName, coloredDescription, activateMembership])

  // Helper function to render desktop banner content
  const renderDesktopBannerContent = useCallback(() => {
    if (!accountEligibleTier) return null

    if (accountEligibleTier === 'bronze') {
      return (
        <LayoutContainer>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" gap="8 20" verticalAlign="center" wrap="wrap">
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <LoyaltyProductLogo height={24} />
              <LoyaltyTierPill tier={accountEligibleTier} kind="primary" icon={null}>{pillMessage}</LoyaltyTierPill>
            </Group>
            <BodyText variant="medium">
              Even more reasons to activate your membership and book today!
              {' '}
              <TextLink size="medium" onClick={activateMembership}>Join for free</TextLink>
            </BodyText>
          </Group>
        </LayoutContainer>
      )
    }

    return (
      <LayoutContainer>
        <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" gap="8 20" verticalAlign="center" wrap="wrap">
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <LoyaltyProgramNameLogo height={24} />
            <LoyaltyTierPill tier={accountEligibleTier} kind="primary">{pillMessage}</LoyaltyTierPill>
          </Group>
          <BodyText variant="medium">
            {!!givenName && <>{givenName}, you've got</>}
            {!givenName && <>You've got</>}
            {' '}
            <Colour value={`tier-${accountEligibleTier}-light-contrast`} as="b">
              {coloredDescription}
            </Colour>
            {' '}
            <TextLink size="medium" onClick={activateMembership}>Activate {getLoyaltyTierDisplayName(accountEligibleTier)} Status free</TextLink>
          </BodyText>
        </Group>
      </LayoutContainer>
    )
  }, [accountEligibleTier, pillMessage, givenName, coloredDescription, activateMembership])

  if (!accountEligibleTier || accountEligibleTierLoading || accountEligibleTierError || accountEligibleTier === 'platinum') return null

  return (
    <Container className={`tier-${accountEligibleTier}`}>
      {/* Mobile infinite scroll version */}
      <MobileScrollContainer>
        <ScrollingContent>
          {/* Duplicate content multiple times for seamless infinite scroll */}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
          {renderMobileBannerContent()}
        </ScrollingContent>
      </MobileScrollContainer>

      {/* Desktop/tablet version */}
      <DesktopContainer>
        {renderDesktopBannerContent()}
      </DesktopContainer>
    </Container>
  )
}

export default LoyaltyMembershipActivationPackageUpgradeBanner
